import { useCurrentUser, useToastProvider } from '@qualio/ui-components';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../../../../api/document';
import { DocumentStatus } from '../../../../../api/model/document';
import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';
import { CredentialConfirmationModal } from '../../../CredentialConfirmationModal';
import {
  ChangeStatusErrorResponseSchema,
  getDefaultFailureMessage,
} from '../../../DocumentWorkspace/WorkspaceV2/BulkActionsV2/BulkActionButtons/ChangeStatusButtons/ChangeStatusService';
import { DocumentOverviewContext } from '../../Context';
import { shouldRenderRetireButton } from '../../RenderLogic';

type RetireActionProps = {
  renderClickableItem: (onClick: () => void) => JSX.Element;
};

export const RetireAction = ({ renderClickableItem }: RetireActionProps) => {
  const { t } = useTranslation();
  const [isRetireModalOpen, setIsRetireModalOpen] = useState(false);
  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );
  const { setDisabled } = useContext(ButtonGroupContext);
  const currentUser = useCurrentUser();
  const { showToast } = useToastProvider();

  const commonToastProps = {
    id: 'retire-toast',
    replace: true,
  };

  const openConfirmationModal = () => {
    setIsRetireModalOpen(true);
    setDisabled(true);
  };

  const closeConfirmationModal = () => {
    setIsRetireModalOpen(false);
    setDisabled(false);
  };

  if (!shouldRenderRetireButton(qualioDocument, currentUser)) {
    return null;
  }

  const retireDoc = async (email: string, password: string, comment = '') => {
    const payload = {
      status: DocumentStatus.Retired,
      email,
      password,
      comment,
    };
    await documentApi
      .changeStatus(qualioDocument.id, payload)
      .then(() => {
        showToast({
          'data-cy': 'retire-action-notify-success-toast',
          ...commonToastProps,
          description: t('success.successfullyRetiredDocument'),
          status: 'success',
          title: t('notifications.success'),
        });
        refetchDocumentOverviewData();
        closeConfirmationModal();
      })
      .catch((error: unknown) => {
        throw error;
      });
  };

  const handleRetireDocumentError = (error: unknown) => {
    const parsedError = ChangeStatusErrorResponseSchema.safeParse(error);
    showToast({
      'data-cy': 'retire-action-notify-error-toast',
      ...commonToastProps,
      status: 'error',
      title: t('notifications.failedToRetire'),
      description: parsedError.success
        ? parsedError.data
        : getDefaultFailureMessage(),
    });
  };

  return (
    <>
      {renderClickableItem(openConfirmationModal)}
      <CredentialConfirmationModal
        isOpen={isRetireModalOpen}
        onCloseModal={closeConfirmationModal}
        modalHeading={t('modals.retireDocument')}
        modalText={
          <>
            {t('modals.retiringDocumentTrainingImpact')}
            <br />
            <br />
            {t('modals.enterDigitalSignatureRetire')}
          </>
        }
        buttonActionLabel={t('buttons.retire')}
        handleError={handleRetireDocumentError}
        buttonProps={{ isDestructive: true }}
        handleStatusChange={retireDoc}
      />
    </>
  );
};
